import {useCallback, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getSecondsOfDay } from '../../../../../Apis/functions';
import {ALL_TIME_TO, CUSTOMIZED_MODEL, DEFAULT_TIME} from '../Model/Models';

const Customized = ({ customizedList, setCustomizedList }) => {
    const { t } = useTranslation();

    const [item, setItem] = useState({...CUSTOMIZED_MODEL});
    const [evaluateIsValid, setEvaluateIsValid] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    useEffect(() => {
        if (item?.fromValue && item?.toValue && item.fromValue > item.toValue) {
            setItem((prev) => ({ ...prev, toValue: null }));
            setEmptyFields(true)
        }
    }, [item.fromValue, item.toValue]);

    const isValidTo = useMemo(() => {
        if (!evaluateIsValid) return true;
        if (!item?.fromValue || !item?.toValue) return true;

        const fromSeconds = getSecondsOfDay(item.fromValue);
        const toSeconds = getSecondsOfDay(item.toValue);

        if (fromSeconds === null || toSeconds === null) return false;

        return toSeconds > fromSeconds;
    }, [evaluateIsValid, item.fromValue, item.toValue]);

    function deepEqual(obj1, obj2) {
        if (obj1 === obj2) return true;

        if (
            typeof obj1 !== 'object' ||
            typeof obj2 !== 'object' ||
            obj1 === null ||
            obj2 === null
        ) {
            return false;
        }

        const keys1 = Object.keys(obj1).slice(0, 4);
        const keys2 = Object.keys(obj2).slice(0, 4);

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
            if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
        }

        return true;
    }

    const handleCheck = (event) => {
        const value = event.target.value;

        setItem((prev) => {
            let updatedList = [...prev.checkedZones];

            if (event.target.checked) {
                updatedList = [...updatedList, value];
            }
            else {
                updatedList = updatedList.filter((x) => x !== value);
            }

            return { ...prev, checkedZones: updatedList };
        });
    };

    const resetState = () => {
        setItem({...item.clear()})
        setEmptyFields(false);
        setEvaluateIsValid(false)

    };

    const selectAllDays = (e) => {
        const checked = e.target.checked;

        setItem((prev) => ({
            ...prev,
            allDays: checked,
            dayArray: checked ? [1, 2, 3, 4, 5, 6, 0] : [],
        }));
    };

    const handleAllTime = (event) => {
        const checked = event.target.checked;

        setItem((prev) => ({
            ...prev,
            allTime: checked,
            fromValue: DEFAULT_TIME,
            toValue: checked ? ALL_TIME_TO : DEFAULT_TIME,
        }));
    };

    const handleCLick = async () => {
        setEvaluateIsValid(true);
        if (!isValidTo) {
            setEmptyFields(true);
            return;
        }
        if (!item.isValid()) {
            toast.error(t('please_fill_all_fields'));
            setEmptyFields(true);
            return;
        }

        let updateArray = [...customizedList];

        for (let i = 0; i < item.checkedZones.length; i++) {
            for (let j = 0; j < item.dayArray.length; j++) {
                const x = {
                    zoneId: item.checkedZones[i].split(',')[0],
                    zoneName :item.checkedZones[i].split(',')[1],
                    dayId: item.dayArray[j],
                    from: moment(item.fromValue).format('HH:mm:ss'),
                    to: moment(item.toValue).format('HH:mm:ss'),
                    added: false,
                    new: true,
                };

                if (!updateArray.some((item) => deepEqual(item, x))) {
                    updateArray.push(x);
                }
            }
        }

        setCustomizedList(updateArray);
        resetState();
    };

    const handleClose = () => {
        setCustomizedList([...customizedList.filter((e) => e.added)]);
        resetState();
    };

    const handleRemoveAccess = (index) => {
        setCustomizedList((prevArray) => prevArray.filter((item, i) => i !== index));
    };

    const handleConfirm = () => {
        setCustomizedList((prevArray) => prevArray.map((item) => ({ ...item, added: true })));
    };

    const handleAdded = () => {
        return customizedList.map((item) => ({
            ...item,
            added: true,
            new: true,
        }));
    };

    /**
     * IMPORTANT:
     * These setters MUST support:
     * - setX(newArray)
     * - setX(prev => newArray)
     * Because CustomTreeView and CustomDaySelector call them that way.
     */
    const setChecked = useCallback((valueOrFn) => {
        setItem((prev) => {
            const nextChecked =
                typeof valueOrFn === 'function'
                    ? valueOrFn(prev.checkedZones || [])
                    : valueOrFn;

            return { ...prev, checkedZones: nextChecked };
        });
    }, [setItem]);

    const setDayArray = useCallback((valueOrFn) => {
        setItem((prev) => {
            const nextDays =
                typeof valueOrFn === 'function'
                    ? valueOrFn(prev.dayArray || [])
                    : valueOrFn;

            return { ...prev, dayArray: nextDays };
        });
    }, [setItem]);


    return {
        item,
        setItem,
        handleCheck,
        selectAllDays,
        handleCLick,
        handleClose,
        handleAllTime,
        handleConfirm,
        handleRemoveAccess,
        handleAdded,
        emptyFields,
        setEmptyFields,
        setChecked,
        setDayArray,
        isValidTo
    };
};

export default Customized;
